import { useEffect, useState } from "react";
import Auth from "../../../Auth";
import { CUSTOM_STYLE_PRESET_OPTION, StylePresetOption } from "./types";

interface PresetsResponse {
  presets: StylePresetOption[];
  [key: string]: unknown;
}

interface UseStylePresetsResult {
  presets: StylePresetOption[];
  presetsLoading: boolean;
  presetsError: string;
  /** The raw /presets response, for endpoint-specific extras (roles, aspectRatios, titleModes). */
  meta: Record<string, unknown>;
  stylePresetId: string;
  setStylePresetId: (id: string) => void;
  /** Editable style-prompt text for the current preset. */
  stylePresetText: string;
  setStylePresetText: (value: string) => void;
  resetStylePresetText: () => void;
  isStylePresetTextDefault: boolean;
}

/**
 * Fetches the curated style presets for one `/generate` family and manages the per-preset editable
 * prompt text (seeded from each preset's default, restorable via "Reset to default").
 */
export function useStylePresets(presetsEndpoint: string): UseStylePresetsResult {
  const [presets, setPresets] = useState<StylePresetOption[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [presetsError, setPresetsError] = useState("");
  const [meta, setMeta] = useState<Record<string, unknown>>({});
  const [stylePresetId, setStylePresetId] = useState("");
  const [textById, setTextById] = useState<Record<string, string>>({});
  const [defaultTextById, setDefaultTextById] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    Auth.fetchWithIdToken(presetsEndpoint, {})
      .then((response: PresetsResponse) => {
        if (cancelled) return;
        setPresets(response.presets);
        setMeta(response);
        setStylePresetId((current) => current || response.presets[0]?.id || "");
        const defaults = Object.fromEntries(
          [...response.presets, CUSTOM_STYLE_PRESET_OPTION].map((preset) => [preset.id, preset.text]),
        );
        setTextById(defaults);
        setDefaultTextById(defaults);
        setPresetsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPresetsError(err instanceof Error ? err.message : "Failed to load style presets");
        setPresetsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [presetsEndpoint]);

  return {
    presets,
    presetsLoading,
    presetsError,
    meta,
    stylePresetId,
    setStylePresetId,
    stylePresetText: textById[stylePresetId] ?? "",
    setStylePresetText: (value: string) => setTextById((current) => ({ ...current, [stylePresetId]: value })),
    resetStylePresetText: () => setTextById((current) => ({ ...current, [stylePresetId]: defaultTextById[stylePresetId] ?? "" })),
    isStylePresetTextDefault: (textById[stylePresetId] ?? "") === (defaultTextById[stylePresetId] ?? ""),
  };
}
